import React, { forwardRef } from 'react';
import { Box } from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/store';
import { PdfContainer } from 'src/common/PdfComponents/PdfContainer';
import FifthCardScannedPagesPDF from 'src/content/siteAudit/PdfView/FifthCardScannedPages';
import ForthCardOfTopIssuesPDF from 'src/content/siteAudit/PdfView/ForthCardOfTopIssues';
import { SideBarCSS } from '../styles/style';
import {
  selectHasAnySections,
  selectSectionVisibilityMap,
  selectPDFConfig
} from '../redux/selectors';
import { NoSectionsPlaceholder } from './NoSectionsPlaceholder';
import { PDFSection } from '../types';
import { SectionWrapper } from './SectionWrapper';
import { ReportLogo } from './ReportLogo';
import { ReportMetadata } from './ReportMetadata';
import { AISummaryPlaceholder } from './AISummaryPlaceholder';
import { LocalSeoPDFContainer } from './LocalSeoPDFContainer';
import { PDF_TEXTS } from '../utils/texts';

/**
 * PreviewCanvas Component
 * Main rendering area for the PDF content.
 */
export const PreviewCanvas = forwardRef<HTMLDivElement, any>((props, ref) => {
  const {
    siteAuditDetails,
    userDetails,
    compareDate,
    pdfIconBase64,
    activePlanName,
    planDetails
  } = useSelector((state: RootState) => ({
    siteAuditDetails: state.siteAudit.siteAuditDetails as any,
    userDetails: state.counter.userDetails as any,
    compareDate: state.siteAudit.compareDate ?? 0,
    pdfIconBase64: state.siteAudit.pdfIconBase64,
    activePlanName: state.user.activePlan,
    planDetails: state.user.planDetails
  }));

  const hasAnySections = useSelector(selectHasAnySections);
  const sectionVisibility = useSelector(selectSectionVisibilityMap);
  const { aiSummary } = useSelector(selectPDFConfig);
  const currentDate = new Date().getTime();

  return (
    <div ref={ref} className="PDF" style={SideBarCSS.canvasWrapper}>
      <Box sx={SideBarCSS.canvasPage(props.isExporting)}>
        <ReportLogo
          selectedIcon={props.selectedIcon}
          pdfIconBase64={pdfIconBase64}
          activePlanName={activePlanName}
          planDetails={planDetails}
        />

        {/* MAIN REPORT TITLE */}
        <h1 style={SideBarCSS.canvasTitle}>
          {PDF_TEXTS.preview.canvas.seoPerformanceOverview}
        </h1>

        <ReportMetadata
          url={siteAuditDetails?.url || props.reportMetaData?.siteUrl}
          compareDate={compareDate}
          currentDate={currentDate}
          dateFormat={
            userDetails?.date_format || props.reportMetaData?.dateFormat
          }
        />

        {/* CONTENT MODULES */}
        {hasAnySections ? (
          <Box sx={SideBarCSS.canvasContentColumn}>
            {/* {(aiSummary?.enabled || !props.isExporting) && ( */}
            {sectionVisibility[PDFSection.AI_SUMMARY] && (
              <AISummaryPlaceholder
                interactive={props.interactive}
                isExporting={props.isExporting}
                enabled={true}
                format={aiSummary?.format}
                tone={aiSummary?.tone}
                previewText={aiSummary?.previewText}
                onRefresh={props.onRefreshAiSummary}
                isRefreshing={props.isRefreshingAiSummary}
              />
            )}

            {/* <SectionWrapper interactive={props.interactive}> */}
            <PdfContainer
              isPdfBuilder={true}
              isExporting={props.isExporting}
              reportMetaData={props.reportMetaData}
            />
            {/* </SectionWrapper> */}

            {sectionVisibility[PDFSection.TASKS] && (
              // <SectionWrapper interactive={props.interactive}>
              <ForthCardOfTopIssuesPDF reportMetaData={props.reportMetaData} />
              // </SectionWrapper>
            )}

            {sectionVisibility[PDFSection.SCANNED_PAGES] && (
              // <SectionWrapper interactive={props.interactive}>
              <FifthCardScannedPagesPDF
                siteId={props.siteId}
                compare={props.compare}
                reportMetaData={props.reportMetaData}
              />
              // </SectionWrapper>
            )}

            {sectionVisibility[PDFSection.LOCAL_SEO] && (
              <LocalSeoPDFContainer
                isExporting={props.isExporting}
                error={props.localSeoError}
                loading={props.localSeoLoading}
                isPdfBuilder={true}
                reportMetaData={props.reportMetaData}
              />
            )}
          </Box>
        ) : (
          <NoSectionsPlaceholder />
        )}
      </Box>
    </div>
  );
});

PreviewCanvas.displayName = 'PreviewCanvas';
